/**
 * Design tokens — the single source of colour and shape for every surface:
 * the web app (mirrored into `apps/web/src/styles/tokens.css`, kept honest by
 * `theme.test.ts`) and the transactional email templates, which cannot use CSS
 * variables and read these values directly.
 *
 * The reasoning behind each choice lives in `apps/web/DESIGN_SYSTEM.md`.
 * Two things are deliberately absent:
 *
 *   - **No shadow tokens.** On a `#000000` ground a drop shadow is invisible,
 *     so stacking them is decoration that renders as nothing. Separation comes
 *     from a hairline and a step in surface lightness.
 *   - **No glow token.** A neon halo around controls is the single fastest way
 *     to make a dark interface look machine-generated.
 */
export const colors = {
    /** Pure OLED black. The panel switches these pixels off entirely. */
    background: '#000000',
    /** Cards, sheets, the navigation bar. */
    surface: '#0A0D0C',
    /** Inputs, chips, inner wells. */
    surfaceRaised: '#101413',
    /** Pressed and hovered states only. */
    surfacePressed: '#161B19',
    /** Every divider and surface border. */
    border: '#1E2422',
    /** Outline buttons and focused inputs. */
    borderStrong: '#2A322F',
    textPrimary: '#FFFFFF',
    /** Labels, secondary values, body copy. */
    textSecondary: '#97A09C',
    /** Captions, placeholders, inactive tabs. */
    textTertiary: '#7A837F', // 5.4:1 on the ground, 4.8:1 on an input well; #5F6864 measured 3.7:1
    /**
     * Brand mint. Permitted in exactly three roles: the one primary action on a
     * screen, a rise in value, and the active navigation tab.
     */
    accent: '#00F5D4',
    accentPressed: '#00D2B4',
    /** Text sitting on a mint fill. */
    accentInk: '#00110E',
    /** A rise. Same hue as the accent — separated from it by form, not colour. */
    success: '#00F5D4',
    /** A fall, and destructive actions. */
    danger: '#FF4D6D',
    dangerPressed: '#E53F5D',
    /** Awaiting review, unverified, warning. */
    warning: '#FFB86B',
};
export const fontFamily = {
    /**
     * Golos Text is a Cyrillic-first face from Paratype. Inter's Cyrillic is an
     * afterthought and it shows in the lowercase к, д and з — which matters in an
     * interface that is Russian by default.
     */
    sans: [
        'Golos Text',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica Neue',
        'Arial',
        'sans-serif',
    ],
    /** Carries every numeral that shares a column with another numeral. */
    mono: [
        'JetBrains Mono',
        'ui-monospace',
        'SFMono-Regular',
        'SF Mono',
        'Menlo',
        'Consolas',
        'monospace',
    ],
};
/** Two radii, plus one for inputs. More than three reads as careless. */
export const radius = {
    /** Cards, sheets, panels. */
    card: '8px',
    /** Inputs and chips. */
    md: '6px',
    /** Pills, buttons, avatars, badges. */
    pill: '999px',
};
/** Directional colours for price change and P&L. */
export const direction = {
    up: colors.success,
    down: colors.danger,
    flat: colors.textSecondary,
};
export const theme = {
    colors,
    fontFamily,
    radius,
    direction,
};
//# sourceMappingURL=theme.js.map